import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { subTestID } from '../../utils/testId';
import type { CodeInputCellsProps } from './CodeInput.types';
import {
  codeInputCellPosition,
  codeInputCellTextClass,
  codeInputCellVariants,
  codeInputGroupClass,
} from './CodeInput.variants';

/**
 * The six joined cells, drawn ONCE for both platforms.
 *
 * The two `CodeInput` forks differ only in how the digits are collected — web
 * hands the job to `input-otp` (paste, autofill, the password-manager
 * shuffle), native to a transparent `TextInput` laid over the strip — and the
 * anatomy of the strip itself is the part that must not drift. It lives here
 * so neither fork owns a copy of the border collapse, the outer radius, or
 * which cell reads as active.
 *
 * Presentational only: no state, no handlers, no focus. Whatever the fork
 * knows about focus and selection arrives as `active` (`null` when the field
 * is not focused).
 */
export function CodeInputCells({ chars, active, testID }: CodeInputCellsProps) {
  const length = chars.length;
  return (
    <View className={codeInputGroupClass}>
      {chars.map((char, index) => (
        <View
          key={index}
          testID={subTestID(testID, `cell-${index}`)}
          className={codeInputCellVariants({
            position: codeInputCellPosition(index, length),
            active: active != null && index >= active.start && index < active.end,
            // The first active cell's left edge is this cell's right border.
            beforeActive: active != null && index === active.start - 1,
          })}>
          <Text className={codeInputCellTextClass}>{char ?? ''}</Text>
        </View>
      ))}
    </View>
  );
}
(CodeInputCells as { displayName?: string }).displayName = 'CodeInputCells';
